/**
 * The eight filters, in one place.
 *
 * The values and shapes here follow the API, not the design canvas: `age_ranges` is a
 * list of bands rather than the slider drawn in 16c, and `has_fanclub` is yes/no rather
 * than the three rows drawn in 16g, because that is what the endpoint accepts.
 *
 * `explain` is optional by design — the canvas gives a second line only where a label
 * is not self-explanatory, and inventing copy for the rest would be worse than leaving
 * the row single-line.
 */

export type FilterOption = {
  value: string;
  /** Message id for the option's label. */
  label: string;
  /** Message id for the optional 11.5px second line. */
  explain?: string;
  /** A semantic colour token, drawn as a dot before the label. */
  dot?: string;
  /** Hidden from anyone who has not verified their age. */
  needsVerifiedAge?: boolean;
  /** A guest picking this gets the login popin; the free tier stays open (BC9). */
  guestLocked?: boolean;
};

export type FilterDefinition = {
  key: string;
  /** Message id for the filter's name — used on the box, the row and the sheet title. */
  title: string;
  /** Message id for the sheet's one-line subtitle. */
  subtitle: string;
  /** Message id for the "do not filter by this" row at the top of the sheet. */
  clearRow: string;
  options: FilterOption[];
  /** Long lists get a search field and scroll under it. */
  search?: string;
  /** Stored as a "yes"/"no" string rather than a list. */
  singleValue?: boolean;
};

export const THERMOMETER: FilterDefinition = {
  key: "thermometers",
  // The app already uses this id as the thermometer's label; no need for a new one.
  title: "uploader__confirmation_title",
  subtitle: "filter__subtitle_thermometers",
  clearRow: "filter__any_thermometer",
  options: [
    { value: "soft", label: "filter__thermometer_soft", explain: "filter__explain_thermometer_soft", dot: "bg-thermometer-soft" },
    { value: "hot", label: "filter__thermometer_hot", explain: "filter__explain_thermometer_hot", dot: "bg-thermometer-sexy", guestLocked: true },
    { value: "hard", label: "filter__thermometer_hard", explain: "filter__explain_thermometer_hard", dot: "bg-thermometer-explicit", needsVerifiedAge: true, guestLocked: true },
  ],
};

export const GENDER: FilterDefinition = {
  key: "genders",
  title: "profile__gender",
  subtitle: "filter__subtitle_genders",
  clearRow: "filter__any_gender",
  options: [
    { value: "man", label: "gender_sought__label__man" },
    { value: "woman", label: "gender_sought__label__woman" },
    { value: "couple", label: "gender_sought__label__couple" },
  ],
};

/** The two that stay on the feed. */
export const ON_FEED: FilterDefinition[] = [THERMOMETER, GENDER];

/** The six behind the more-filters chip. The chip's badge counts these, not values. */
export const MORE_FILTERS: FilterDefinition[] = [
  {
    key: "age_ranges",
    title: "filter__title_age_range",
    subtitle: "filter__subtitle_age_ranges",
    clearRow: "filter__any_age",
    options: [
      { value: "18-20", label: "age_range__value_label__18-20" },
      { value: "21-25", label: "age_range__value_label__21-25" },
      { value: "26-30", label: "age_range__value_label__26-30" },
      { value: "31-35", label: "age_range__value_label__31-35" },
      { value: "36-40", label: "age_range__value_label__36-40" },
      { value: "41-45", label: "age_range__value_label__41-45" },
      { value: "46-50", label: "age_range__value_label__46-50" },
      { value: "51-60", label: "age_range__value_label__51-60" },
      { value: "60", label: "age_range__value_label__60" },
    ],
  },
  {
    key: "languages",
    title: "filter__title_spoken_language",
    subtitle: "filter__subtitle_languages",
    clearRow: "filter__any_language",
    search: "filter__search_language",
    options: [
      { value: "fr", label: "spoken_language__label__french" },
      { value: "en", label: "spoken_language__label__english" },
      { value: "nl", label: "spoken_language__label__dutch" },
      { value: "pt", label: "spoken_language__label__portuguese" },
      { value: "it", label: "spoken_language__label__italian" },
      { value: "es", label: "spoken_language__label__spanish" },
      { value: "de", label: "spoken_language__label__german" },
    ],
  },
  {
    key: "website_uses",
    title: "filter__title_website_uses",
    subtitle: "filter__subtitle_website_uses",
    clearRow: "filter__any_website_use",
    options: [
      { value: "share", label: "website_uses__label__share" },
      { value: "photos", label: "website_uses__label__photo" },
      { value: "videos", label: "website_uses__label__video" },
      { value: "chat", label: "website_uses__label__chat" },
    ],
  },
  {
    key: "relationship_sought",
    title: "filter__title_relationship_sought",
    subtitle: "filter__subtitle_relationship_sought",
    clearRow: "filter__any_relationship",
    options: [
      { value: "virtual", label: "relationship_sought__label__virtual" },
      { value: "real", label: "relationship_sought__label__real" },
      { value: "friendship", label: "relationship_sought__label__friendship" },
      { value: "sexting", label: "relationship_sought__label__sexting" },
    ],
  },
  {
    // RF8 — restored from master, which offered it on the desktop row; the
    // chip-era catalog had dropped it while persisted values kept riding
    // requests with no UI to clear them.
    key: "is_online",
    title: "filter__title_online",
    subtitle: "filter__subtitle_online",
    clearRow: "filter__any_online",
    singleValue: true,
    options: [
      { value: "yes", label: "filter__online_yes" },
      { value: "no", label: "filter__online_no" },
    ],
  },
  {
    key: "has_fanclub",
    title: "filter__title_has_fanclub",
    subtitle: "filter__subtitle_has_fanclub",
    clearRow: "filter__any_fanclub",
    singleValue: true,
    options: [
      { value: "yes", label: "has_fanclub__label__yes", explain: "filter__explain_fanclub_yes" },
      { value: "no", label: "has_fanclub__label__no", explain: "filter__explain_fanclub_no" },
    ],
  },
];

export const ALL_FILTERS = [...ON_FEED, ...MORE_FILTERS];

/** Empty means "do not filter by this", so it does not count as a filter that is on. */
export function isActive(definition: FilterDefinition, value: any): boolean {
  if (definition.singleValue) return value === "yes" || value === "no";
  return Array.isArray(value) && value.length > 0;
}

/** How many of the five off-feed filters are on. Filters, never values. */
export function countMoreFilters(filters: any): number {
  return MORE_FILTERS.filter((definition) => isActive(definition, filters?.[definition.key])).length;
}

export function selectedOptions(definition: FilterDefinition, value: any): FilterOption[] {
  if (!isActive(definition, value)) return [];
  const chosen = definition.singleValue ? [value] : value;
  return definition.options.filter((option) => chosen.includes(option.value));
}
